/* eslint-disable no-param-reassign */
/* eslint-disable max-len */
/* eslint-disable prefer-destructuring */
import React, {
  useEffect, useState, useCallback, useContext,
} from 'react';
import { useParams } from 'react-router-dom';
import {
  Button, Snackbar, DialogTitle, Dialog, Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InputLabel from '@mui/material/InputLabel';
import { GridActionsCellItem } from '@mui/x-data-grid-premium';

import logger from '../../utils/logger';
import {
  getRowIdxForId, getLocalStorageTablePref, updateTablePreferenceLocalStorage, diffListOfObjects,
} from '../../utils/index';
import ClientTextField from '../clientFields/ClientTextField';
import ClientDropDown from '../clientFields/ClientDropDown';
import APIWrapper from '../../utils/graphqlwrapper';
import { sourceColumns, channelCategoryEnum } from '../../utils/schemaInfo';
import { getAndFormat30DaysAgo } from '../../utils/formatters';

import AppContext from '../../context';

import { createSourceRows } from '../../utils/tableHelpers';
import AdvancedSearchV2 from '../AdvancedSearchV2';
import AdvancedSearchChip from '../AdvancedSearchChip';
import ServerTable from '../ServerTable';
import { CustomDatePicker } from '../DateAndTimePickers';

// eslint-disable-next-line camelcase
const channel_category = ['Select one', ...channelCategoryEnum];
const structure = {
  // eslint-disable-next-line camelcase
  channel_category,
};

const initColumnVisibilityModel = { from_goop: false };

export default function Sources() {
  const { client } = useParams();
  const legacySwitchDateInEpoch = new Date('2026-04-07T20:00:00Z').getTime();
  const { authenticated, setShouldLogOut } = useContext(AppContext);
  const [snackMessage, setSnackMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackWithConfirm, setSnackWithConfirm] = useState(false);
  // used to identify when the source value is changing and we need to make sure the id matches the source
  const [existingSourceUpdateId, setExistingSourceUpdateId] = useState(false);
  const [previousUpdatedId, setPreviousUpdatedId] = useState();
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initColumnVisibilityModel);
  // used by snackbar action handler to delete source/update source on codes on user confirm
  // {id: str, source: str}
  const [deleteSourceData, setDeleteSourceData] = useState({});
  // used to maintain correct source value on both Sources and Codes tables
  // (on existing source edit); {oldValue: str, newValue: str}
  const [updatedSource, setUpdatedSource] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // if false, dialog in edit state, if true in create state; used to diff which mutation to call
  const [isDialogInCreateState, setisDialogInCreateState] = useState(false);
  // tracks data in create/edit modal
  const [editRowData, setEditRowData] = useState({});
  const [newSourceRowCounter, setNewSourceRowCounter] = useState(1);
  const [sourcesPageState, setSourcesPageState] = useState({
    page: 0, // mui pages are 0 indexed
    pageSize: 50,
    data: [],
    total: 0,
  });

  const thirtyDaysAgoString = getAndFormat30DaysAgo();

  // nicely formatted query for our API
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState({
    client_id: {
      eq: client,
    },
    and: {
      createdAt: {
        gte: thirtyDaysAgoString,
      },
    },
  });

  // used for chips
  const [userCriteria, setUserCriteria] = useState([]);

  const defaultSearch = [
    {
      col: 'createdAt',
      op: 'gte',
      criteria: thirtyDaysAgoString,
      children: [],
      id: 1,
    },
  ];

  // search criteria user has entered in the advanced search
  const [currUserSearch, setCurrUserSearch] = useState(defaultSearch);

  const deleteChip = (parentIndex) => {
    /**
     * This function is used to delete a chip from the advanced search.
     *
     * @param {number} parentIndex - the index of the chip to be deleted
     */
    setCurrUserSearch((prevState) => {
      const newState = [...prevState];
      newState.splice(parentIndex, 1);
      return newState;
    });
  };

  const defaultSort = {
    field: 'createdAt',
    direction: 'desc',
  };

  const [advancedSearchSort, setAdvancedSearchSort] = useState(defaultSort);

  // default/locked criteria the user can't delete - search must be scoped ot current client
  const lockedCriteria = [
    {
      col: 'client_id',
      op: 'eq',
      criteria: client,
    },
  ];

  const getCodesBySource = async (sourceValue) => {
    /**
     * This function is used to get the codes associated with a source for a specific client.
     *
     * @param {string} sourceValue - the source value to be searched for
     */
    const codes = await APIWrapper.queryApi(
      { query: 'codesBySource' },
      false,
      { source: sourceValue, filter: { client_id: { eq: `${client}` } }, limit: 5000 },
    )
      .then((resp) => resp)
      .catch((err) => logger.error(err));
    return codes.data[0];
  };

  const deleteFunc = (id) => {
    /**
     * This function is used to delete a source from the database.
     *
     * @param {string} id - the id of the source to be deleted
     */
    if (authenticated) {
      APIWrapper.queryApi(
        { mutation: 'deleteSources' },
        setShouldLogOut,
        { input: { id } },
      )
        .then((resp) => {
          const results = resp.data[0] || null;
          const sourceValue = results[0].source;
          if (results && !existingSourceUpdateId) {
            setSnackMessage(`${sourceValue} was deleted.`);
            setOpenSnackbar(true);
            // 2 cases
            getCodesBySource(sourceValue)
              .then((codes) => {
                if (!codes.length) {
                  // case1, no matching codes
                  setUpdatedSource({});
                } else {
                  // case2, there are codes attached to source being updated
                  const sourceUpdateData = {
                    oldValue: results[0],
                    newValue: { source: null, channel_category: null },
                  };
                  setUpdatedSource(sourceUpdateData);
                }
              })
              .catch((err) => {
                logger.error(err);
              });
            const latestSources = sourcesPageState.data.filter(
              (r) => r.source !== results[0].source,
            );
            setSourcesPageState({ ...sourcesPageState, data: latestSources });
          }
        });
    }
  };

  async function updateCodesFunc(columnsUpdating, updatedCodeIDs, newSourceValue) {
    /**
     * This function is used to update codes in the database when source or channel category are changed. This is handling
     * both the case where the source value is updated to a new string and the case where the source
     * value is cleared to null.
     *
     * @param {array} columnsUpdating - the columns on the code we're updating, either source or channel_category
     * @param {array} updatedCodeIDs - the code Id(s) we're updating
     * @param {string} newSourceValue - the new source value
     */
    const updatedCodes = [];
    // build input for mutation based on whether we're updating source or channel category
    // need to handle if both change at the same time
    const input = {};
    if (columnsUpdating.includes('source')) {
      input.source = newSourceValue.source;
    }
    if (columnsUpdating.includes('channel_category')) {
      input.channel_category = newSourceValue.channel_category;
    }
    // eslint-disable-next-line no-restricted-syntax
    for await (const code of updatedCodeIDs) {
      await APIWrapper.queryApi(
        { mutation: 'updateCodes' },
        setShouldLogOut,
        { input: { id: code, ...input } },
      )
        .then((resp) => {
          updatedCodes.push(resp);
          setSnackMessage(`Source updated for ${updatedCodes.length} ${updatedCodes.length === 1 ? 'code' : 'codes'}.`);
          setOpenSnackbar(true);
        })
        .catch((err) => {
          logger.error(err);
          const errMsg = err.errors[0].message || 'Changes to the codes associated with this source could not be saved.';
          setSnackMessage(errMsg);
          setOpenSnackbar(true);
        });
    }
  }

  const handleDeleteClick = (event, row) => {
    /**
     * This function is used to handle the delete click event.
     *
     * @param {object} event - the event object
     * @param {object} row - the row being deleted
     */
    const curRow = row.row;
    const canDeleteRow = !curRow.from_goop;
    let requiresUserConfirm = false;
    // check for codes associated with source
    getCodesBySource(curRow.source)
      .then((codes) => {
        if (codes.length) {
          requiresUserConfirm = true;
        }
        if (!canDeleteRow) {
          setOpenSnackbar(true);
          setSnackMessage('The source was created in Goop and so you need to update it in Goop.');
        } else if (canDeleteRow && requiresUserConfirm) {
          // case where row can be deleted but requires user confirm because affects codes table
          setSnackWithConfirm(true);
          setSnackMessage(`The source you are deleting is associated with ${codes.length} codes currently. If you continue, the source will be deleted and removed from the currently linked codes.`);
          setOpenSnackbar(true);
          setDeleteSourceData({
            id: curRow.id,
            source: curRow.source,
          });
        } else {
          // case where row can be deleted but does not affect codes data
          setSnackWithConfirm(true);
          setSnackMessage('Are you sure you wish to delete this source?');
          setOpenSnackbar(true);
          setDeleteSourceData({
            id: curRow.id,
          });
        }
      })
      .catch((err) => {
        logger.error(err);
      });
  };

  // onclick handler for edit icon in the table actions column
  const handleEditClick = (event, row) => {
    /**
     * This function is used to handle the edit click event, if it is not from Goop then allow the
     * user to edit the row. Otherwise, tell them to edit it in Goop.
     *
     * @param {object} event - the event object
     * @param {object} row - the row being edited
     */
    const curRow = row.row;
    if (!curRow.from_goop) {
      if (curRow.channel_category === 'SMS_Longcode') {
        if (new Date(curRow.createdAt).getTime() < legacySwitchDateInEpoch || new Date(curRow.updatedAt).getTime() < legacySwitchDateInEpoch) {
          // handle P2P/legacy SMS_Longcode sources created before 4/7/2026
          setOpenSnackbar(true);
          setSnackMessage('The source is a SMS Longcode source created before 4/7/2026 and cannot be edited. Please submit a tools help ticket if changes are needed.');
          return;
        }
        // check for SMS_Longcode Sources
        const substrings = curRow.source.split(' | ');
        // Since we're not saving some fields to the DB, let's get the info from the SMS_Longcode source name
        if (substrings.length >= 2) {
          curRow.mailing_name = substrings[0];
          curRow.sms_longcode_source = curRow.source;
        }
      }
      setEditRowData(curRow);
      setIsDialogOpen(true);
    } else {
      setSnackMessage('The source was created in Goop and so you need to update it in Goop.');
      setOpenSnackbar(true);
    }
  };

  const actionCol = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      disableClickEventBubbling: true,
      cellClassName: 'actions',
      disableReorder: true,
      getActions: (row) => [
        <GridActionsCellItem
          icon={(
            <EditIcon
              color={row.row.from_goop || (row.row.channel_category === 'SMS_Longcode' && new Date(row.row.createdAt).getTime() < legacySwitchDateInEpoch) ? 'disabled' : ''}
            />
            )}
          label="Edit"
          className="textPrimary"
          onClick={(ev) => handleEditClick(ev, row)}
          color="inherit"
          size="medium"
        />,
        <GridActionsCellItem
          icon={<DeleteIcon color={row.row.from_goop ? 'disabled' : 'inherit'} />}
          label="Delete"
          onClick={(ev) => handleDeleteClick(ev, row)}
          color="inherit"
          size="medium"
        />,
      ]
      ,
    },
  ];

  const columns = actionCol.concat(sourceColumns);
  const [orderedColumns, setOrderedColumns] = useState(columns);

  function addNewRow() {
    /**
     * This function is used to add a new row to the sources table.
     */
    setisDialogInCreateState(true);
    const newRow = {
      id: '',
      client_id: client,
      source: '',
      channel_category: '',
      from_goop: false,
      mailing_name: '',
      sms_longcode_source: '',
      send_date: null,
    };
    setEditRowData(newRow);
    setIsDialogOpen(true);
  }

  const validateSourceRow = (newRow) => {
    /**
     * This function is used to validate the row data before sending it to the graphql mutation.
     *
     * @param {object} newRow - the source row data to be validated
     * @returns {boolean} - true if the row is valid, false otherwise
     */
    let snackErrors = '';
    if (newRow.channel_category !== 'SMS_Longcode' && !newRow.source) {
      snackErrors += 'Source cannot be empty.';
    }
    const validChannelCategories = structure.channel_category;
    const hasChannelCategoryError = validChannelCategories.indexOf(newRow.channel_category) === -1;
    if (hasChannelCategoryError) {
      snackErrors += ' Please select a Channel Category.';
    }

    if (newRow.channel_category === 'SMS_Longcode') {
      if (!newRow.mailing_name || !newRow.send_date) {
        snackErrors += ' Please fill out all fields before creating a SMS_Longcode source.';
      }
      // use source for mutation to create SMS_Longcode source
      newRow.source = newRow.sms_longcode_source;
    }

    // return errors on front-end
    if (snackErrors) {
      setSnackMessage(snackErrors);
      setOpenSnackbar(true);
      return false;
    }

    // if there aren't any errors
    // remove unused fields used to create source for SMS_Longcode
    delete newRow.sms_longcode_source;
    delete newRow.mailing_name;

    return true;
  };

  const createSource = async (newRow) => {
    /**
     * This function is used to create a new source in the database.
     * It will also be invoked when an existing source's source value was updated
     * because we are creating a new source to ensure we create the correct id of {client}-{source} to prevent duplicates.
     *
     * @param {object} newRow - the row data to be created
     * @returns {boolean} - whether source was created successfully
     */
    // ensure correct id is created for new source
    let createdSuccessfully;
    newRow.id = `${client}-${newRow.source}`;
    if (newRow.codes || newRow.updatedAt) {
      delete newRow.codes;
      delete newRow.updatedAt;
    }
    await APIWrapper.queryApi(
      { mutation: 'createSources' },
      setShouldLogOut,
      { input: newRow },
    )
      .then((resp) => {
        const results = resp.data[0][0] || null;
        if (resp.errors) {
          // check for errors first
          const errMsg = resp.errors[0].message || 'The source could not be created.';
          setSnackMessage(errMsg);
          setOpenSnackbar(true);
          setisDialogInCreateState(false);
          setIsDialogOpen(false);
          createdSuccessfully = false;
        } else if (results) {
          setSnackMessage(`${results.source} was created.`);
          setOpenSnackbar(true);
          if (isDialogInCreateState) {
            const copyRowsUpdated = [results].concat(sourcesPageState.data);
            setSourcesPageState((old) => (
              { ...old, data: copyRowsUpdated, total: old.total + 1 }));
            setisDialogInCreateState(false);
            setIsDialogOpen(false);
          } else {
            // an existing source's source value was updated
            // update existing row value for page state
            const rowIdx = getRowIdxForId(editRowData.id, sourcesPageState.data);
            if (rowIdx > -1) {
              const copyRows = sourcesPageState.data.slice(0);
              copyRows.splice(rowIdx, 1, results);
              setSourcesPageState((old) => ({ ...old, data: copyRows }));
            }
          }
          createdSuccessfully = true;
        }
      })
      .catch((err) => {
        logger.error(err);
        const errMsg = err.errors[0].message || 'The source could not be created.';
        setSnackMessage(errMsg);
        setOpenSnackbar(true);
        setisDialogInCreateState(false);
        setIsDialogOpen(false);
      });
    return createdSuccessfully;
  };

  const updateSource = (copyNewRow, origSource) => {
    /**
     * This function is used to update a source row in the database.
     * NOTE: this is only used when the source value is NOT changing.
     *
     * @param {object} copyNewRow - the row data to be updated
     * @param {object} origSource - the original source data before the update
     */
    APIWrapper.queryApi(
      { mutation: 'updateSources' },
      setShouldLogOut,
      { input: copyNewRow },
    )
      .then((resp) => {
        const results = resp.data[0][0] || null;
        if (results) {
          // update the row in the table manually
          const rowIdx = getRowIdxForId(editRowData.id, sourcesPageState.data);
          if (rowIdx > -1) {
            const copyRows = sourcesPageState.data.slice(0);
            copyRows.splice(rowIdx, 1, editRowData);
            setSourcesPageState((old) => ({ ...old, data: copyRows }));
          }
          // update the corresponding codes if channel cat is changing.
          if (origSource.channel_category !== copyNewRow.channel_category) {
            getCodesBySource(origSource.source)
              .then((codes) => {
                if (codes.length > 0) {
                  const codeIds = [];
                  codes.forEach((code) => {
                    codeIds.push(code.id);
                  });
                  updateCodesFunc(['channel_category'], codeIds, copyNewRow, origSource);
                }
              })
              .catch((err) => {
                logger.error(err);
              });
          }
          setSnackMessage(`${results.source} was updated.`);
          setOpenSnackbar(true);
          setIsDialogOpen(false);
        }

        if (resp.errors) {
          const errMsg = `Changes to the row could not be saved. Error: ${resp.errors[0].message}`;
          setSnackMessage(errMsg);
          setOpenSnackbar(true);
          setIsDialogOpen(false);
        }
      })
      .catch((err) => {
        logger.error(err);
        const errMsg = `Changes to the row could not be saved. Error: ${err.errors[0].message}`;
        setSnackMessage(errMsg);
        setOpenSnackbar(true);
        setIsDialogOpen(false);
      });
  };

  const querySourceBySource = (newSource, fn, prevSource) => {
    /**
     * This function is used to check if a source already exists in the database for this client.
     * If it does not exist, it will execute the function passed in as the fn param
     *
     * @param {object} newSource - the row data to be checked
     * @param {function} fn - the function to be called if the source does not already exist
     * @param {string} prevSource - the previous source name if it's being updated
     */
    const { source } = newSource;
    const filter = { client_id: { eq: client } };

    APIWrapper.queryApi(
      { query: 'sourceBySource' },
      setShouldLogOut,
      { source, filter },
    )
      .then((resp) => {
        if (resp.data && resp.data[0] && resp.data[0].length > 0) {
          const errMsg = 'This source already exists so it cannot be created. If you need to update it, search for it and click the edit icon.';
          setSnackMessage(errMsg);
          setOpenSnackbar(true);
          setIsDialogOpen(false);
        } else if (fn === updateSource) {
          // If a source's source value is updating, we do not want to invoke the updateSource function
          // instead we should invoke createSource to prevent issues with the id value
          setPreviousUpdatedId(prevSource.id);
          const sourceUpdateData = {
            oldValue: prevSource,
            newValue: newSource,
          };
          createSource(newSource).then((result) => {
            if (result) {
              // only do the following if we created the new source successfully
              // otherwise we get unexpected behavior with sources disappearing
              setUpdatedSource(sourceUpdateData);
              setIsDialogOpen(false);
            } else {
              setIsDialogOpen(false);
            }
          });
        } else {
          // make the mutation request
          fn(newSource);
        }
      })
      .catch((err) => {
        logger.error(err);
        const errMsg = `Changes to the row could not be saved. Error: ${err.errors[0].message}`;
        setSnackMessage(errMsg);
        setOpenSnackbar(true);
        setIsDialogOpen(false);
      });
  };

  const saveFunc = (newRow) => {
    /**
     * This function is used to save a source to the database. Handles both create and edit cases.
     *
     * @param {object} newRow - the row data to be saved
     */
    // prep for edit case
    const copyNewRow = { ...newRow };
    // remove createdAt because mutation does not expect it to be passed in.
    if (copyNewRow.createdAt) {
      delete copyNewRow.createdAt;
    }

    if (isDialogInCreateState && authenticated) {
      // create case
      querySourceBySource(newRow, createSource);
    } else if (authenticated) {
      // edit case
      // check for duplicate source only if changing the source value
      const origSource = sourcesPageState.data.filter(
        (r) => r.id === newRow.id,
      );
      if (copyNewRow.source !== origSource[0].source) {
        // handle when source changes, so we need to make sure the id matches source to prevent dupes
        setExistingSourceUpdateId(true);
        querySourceBySource(copyNewRow, updateSource, origSource[0]);
      } else {
        // handle when source value is not changing, so we can just do a normal update
        updateSource(copyNewRow, origSource[0]);
      }
    }
  };

  const handleClose = (event, reason) => {
    /**
     * This function is used to handle the close event from the snackbar.
     */
    if (reason === 'clickaway') {
      return;
    }

    setSnackWithConfirm(false);
    setOpenSnackbar(false);
    setSnackMessage('');
  };

  const handleSnackDeleteConfirm = () => {
    /**
     * This function is used to handle the delete confirm event from the snackbar.
     */
    deleteFunc(deleteSourceData.id);
    setSnackWithConfirm(false);
    setOpenSnackbar(false);
    setSnackMessage('');
  };

  const memoizeNewRow = useCallback(() => {
    addNewRow();
    setNewSourceRowCounter(newSourceRowCounter + 1);
  });

  const actionForDeleteSource = (
    <>
      <Button color="secondary" size="small" onClick={handleSnackDeleteConfirm}>
        Delete
      </Button>
      <IconButton
        size="small"
        aria-label="close"
        color="inherit"
        onClick={handleClose}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const actionDefault = (
    <IconButton
      size="small"
      aria-label="close"
      color="inherit"
      onClick={handleClose}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );

  const handleCloseDialog = () => {
    /**
     * This function is used to handle the close event from the create/edit modal.
     */
    setisDialogInCreateState(false);
    setIsDialogOpen(false);
  };

  const handleModalSave = () => {
    /**
     * This function is used to handle the save event from the create/edit modal.
     */
    const isRowValid = validateSourceRow(editRowData);
    if (isDialogInCreateState && isRowValid) {
      setEditRowData(editRowData);
      saveFunc(editRowData);
    } else if (isRowValid) {
      saveFunc(editRowData);
    }
  };

  // responds to onChange event in order to track updates to the row from the
  // create/edit modal/dialog
  // updates state in this component which will be used to send to the graphql mutation
  const setter = (key, value) => {
    // eslint-disable-next-line no-shadow
    setEditRowData((editRowData) => ({ ...editRowData, [key]: value }));
  };

  useEffect(() => {
    const sourcesColumnVisibility = getLocalStorageTablePref('sources', 'column-visibility');
    if (!sourcesColumnVisibility) {
      updateTablePreferenceLocalStorage('sources', 'column-visibility', initColumnVisibilityModel);
    } else {
      setColumnVisibilityModel(sourcesColumnVisibility);
    }
  }, []);

  useEffect(() => {
    const sourcesColumnsLocalStor = getLocalStorageTablePref('sources', 'column');
    const isSourcesColumnsDefUpdated = !diffListOfObjects(
      sourceColumns,
      sourcesColumnsLocalStor,
    );
    if (!sourcesColumnsLocalStor || isSourcesColumnsDefUpdated) {
      updateTablePreferenceLocalStorage('sources', 'column', orderedColumns);
    } else {
      let elToUpdate = sourcesColumnsLocalStor[0];
      elToUpdate = {
        ...elToUpdate,
        getActions: (row) => [
          <GridActionsCellItem
            icon={(
              <EditIcon
                color={row.row.from_goop || (row.row.channel_category === 'SMS_Longcode' && (new Date(row.row.createdAt).getTime() < legacySwitchDateInEpoch || new Date(row.row.updatedAt).getTime() < legacySwitchDateInEpoch)) ? 'disabled' : ''}
              />
            )}
            label="Edit"
            className="textPrimary"
            onClick={(ev) => handleEditClick(ev, row)}
            color="inherit"
            size="medium"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon color={row.row.from_goop || (row.row.channel_category === 'SMS_Longcode' && (new Date(row.row.createdAt).getTime() < legacySwitchDateInEpoch || new Date(row.row.updatedAt).getTime() < legacySwitchDateInEpoch)) ? 'disabled' : ''} />}
            label="Delete"
            onClick={(ev) => handleDeleteClick(ev, row)}
            color="inherit"
            size="medium"
          />,
        ],
      };
      sourcesColumnsLocalStor[0] = elToUpdate;
      setOrderedColumns(sourcesColumnsLocalStor);
    }
  }, [sourcesPageState]);

  // this handles sync between Sources and Codes tables, to ensure when an existing source value
  // is updated (Sources table) that the corresponding source value (Codes table) also gets updated
  useEffect(() => {
    if (Object.keys(updatedSource).length > 0) {
      getCodesBySource(updatedSource.oldValue.source)
        .then((codes) => {
          if (codes.length > 0) {
            const codeIds = [];
            codes.forEach((code) => {
              codeIds.push(code.id);
            });
            // update codes with new source/channel category data
            if (updatedSource.newValue.channel_category !== updatedSource.oldValue.channel_category) {
              updateCodesFunc(['source', 'channel_category'], codeIds, updatedSource.newValue, updatedSource.oldValue);
            } else {
              updateCodesFunc(['source'], codeIds, updatedSource.newValue, updatedSource.oldValue);
            }
          }
          if (existingSourceUpdateId) {
            // if source's source value was updated, we created the new source with the correct id and
            // associated the codes with the new source, now we will delete the old source
            deleteFunc(previousUpdatedId);
          }
        })
        .catch((err) => {
          logger.error(err);
        });
    }
  }, [updatedSource]);

  useEffect(() => {
    // new SMS_Longcode source structure: Mailing Name | Send Date
    let smsLongcodeSource;
    if (editRowData.channel_category === 'SMS_Longcode') {
      // Since we're not saving some fields to the DB, let's get the info from the SMS_Longcode source name
      // if creating source value then follow this format, otherwise just fetch the source in the DB
      const formattedMailingName = editRowData.mailing_name ? editRowData.mailing_name.trim() : editRowData.mailing_name;
      smsLongcodeSource = `${formattedMailingName} | ${editRowData.send_date}`;
      setter('sms_longcode_source', smsLongcodeSource);
    }
  }, [editRowData.mailing_name, editRowData.send_date]);

  const dialogTitlePrefix = isDialogInCreateState ? 'Creating ' : 'Editing ';

  const getDefaultColumns = () => sourceColumns;

  return (
    <>
      <h2>Source Management</h2>
      <div className="header_table_button_container table-actions">
        <Button data-testid="add-source-btn" variant="contained" onClick={memoizeNewRow}>
          Add source
        </Button>
        {client && (
          <AdvancedSearchV2
            currUserSearch={currUserSearch}
            setCurrUserSearch={setCurrUserSearch}
            searchSetter={setAdvancedSearchQuery}
            sortSetter={setAdvancedSearchSort}
            lockedCriteria={lockedCriteria}
            defaultCriteria={defaultSearch}
            defaultSort={defaultSort}
            columns={sourceColumns}
            lockedColumns={['client_id']}
            setUserChips={setUserCriteria}
          />
        )}
      </div>
      {client && (
        <>
          <AdvancedSearchChip
            columns={sourceColumns}
            lockedCriteria={lockedCriteria}
            defaultCriteria={defaultSearch}
            userCriteria={userCriteria}
            setUserCriteria={setUserCriteria}
            onDelete={(index) => { deleteChip(index); }}
          />
          <ServerTable
            pageState={sourcesPageState}
            updatePageState={setSourcesPageState}
            columns={orderedColumns}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            filters={advancedSearchQuery}
            sortModel={advancedSearchSort}
            queryName="searchSources"
            tablePrefix="sources-adv"
            pinnedColumns={{ pinnedColumns: { left: ['actions'] } }}
            createRows={createSourceRows}
            getDefaultColumns={getDefaultColumns}
            setOrderedColumns={() => { }} // ls/saving prefs is in flux, adding this to prevent errs
          />
        </>

      )}
      <Dialog onClose={handleCloseDialog} open={isDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogTitlePrefix}
          Sources
        </DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignContent: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            flexGrow: 1,
            p: 3,
            m: 1,
            borderRadius: 1,
            gap: '7px',
          }}
        >
          <ClientTextField
            setFunction={setter}
            data={editRowData.client_id}
            attribute="client_id"
            label="Client"
            isDisabled
          />
          <ClientDropDown
            setFunction={setter}
            data={editRowData.channel_category}
            attribute="channel_category"
            label="Channel Category"
            structure={structure}
            testingId="channel-category-select"
            required
          />
          {editRowData.channel_category && editRowData.channel_category !== 'SMS_Longcode'
            && (
              <ClientTextField
                setFunction={setter}
                data={editRowData.source}
                attribute="source"
                label="Source"
                testingId="sourceInput"
              />
            )}
          {editRowData.channel_category === 'SMS_Longcode' && (
            <>
              <ClientTextField
                setFunction={setter}
                data={editRowData.mailing_name}
                attribute="mailing_name"
                label="Mailing Name"
                required
              />
              <InputLabel
                variant="standard"
                htmlFor="send_date"
                required
              >
                Send Date
              </InputLabel>
              <CustomDatePicker
                format="MM/DD/YYYY"
                setCriteria={setter}
                criteria={editRowData.send_date}
                name="send_date"
              />
              <ClientTextField
                setFunction={setter}
                isDisabled
                data={editRowData.sms_longcode_source}
                attribute="sms_longcode_source"
                label="Source"
                helperText="Source will be created automatically for SMS_Longcode sources based on input."
                testingId="sms_longcode_source_input"
              />
            </>
          )}
          <Button
            data-testid="save-source-btn"
            className="datagrid-rightside-buttons"
            variant="contained"
            style={{
              justifyContent: 'center',
              marginTop: '20px',
              marginBottom: '15px',
            }}
            onClick={handleModalSave}
          >
            Save
          </Button>
        </Box>
      </Dialog>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={15000}
        onClose={handleClose}
        message={snackMessage}
        action={snackWithConfirm ? actionForDeleteSource : actionDefault}
      />
    </>
  );
}
